import { array, fn } from '@ember/helper';
import { HStack, Section, VStack } from 'tio-reimagined/components/layout';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import ReSelect from 'tio-reimagined/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import ApproverComments from 'tio-common/components/tuition-assistance/approver-comments';
import AttachmentList from 'tio-common/components/tuition-assistance/forms/upload-attachments/attachment-list';
import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import pageTitle from 'ember-page-title/helpers/page-title';
import PaymentDetailsDrawer from 'tio-employee/components/drawers/payment-details-drawer';
import TioAppearance from 'tio-common/components/tio/appearance';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import TuitionAssistanceApplicationDetailsApprovalQueueStatus from 'tio-common/components/tuition-assistance/application-details/approval-queue-status';
import TuitionAssistanceApplicationDetailsApproverOverview from 'tio-common/components/tuition-assistance/application-details/approver-overview';
import TuitionAssistanceApplicationDetailsEmployeeInformation from 'tio-common/components/tuition-assistance/application-details/employee-information';
import TuitionAssistanceApplicationDetailsProgramStatusApproval from 'tio-common/components/tuition-assistance/application-details/program-status-approval';
import TuitionAssistanceFormsUploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import TuitionAssistanceProgramDetailsAppDetailsAppHistory from 'tio-common/components/tuition-assistance/program-details/app-details/app-history';
import TuitionAssistanceProgramDetailsApplicationInformation from 'tio-common/components/tuition-assistance/program-details/application-information';
import TuitionAssistanceProgramDetailsProgramInfoCard from 'tio-common/components/tuition-assistance/program-details/program-info-card';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';

interface RouteSignature {
  Args: {
    model: TasProgramInstanceModel;
  };
}

const classificationOptions = [
  { label: 'Program Approval Documents', key: 'PROGRAM_APPROVAL_DOCUMENTS' },
  { label: 'Program Completion Documents', key: 'COMPLETION_DOCUMENTS' },
];

export default class TuitionAssistanceProgramInstancesShowIndexRouteComponent extends Component<RouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @tracked uploadModalOpen = false;
  @tracked selectedClassification = 'PROGRAM_APPROVAL_DOCUMENTS';
  @tracked uploadClassificationField = 'PROGRAM_APPROVAL_DOCUMENTS';

  get allAttachments() {
    return this.args.model.tasAssets || [];
  }

  setUploadClassification = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'PROGRAM_APPROVAL_DOCUMENTS';
  };

  <template>
    {{pageTitle (t "tuition_assistance.program_details.default")}}
    <div class="flex sticky top-0 bg-white">
      <TioPageBreadcrumbs class="mb-4 flex-1" as |b|>
        <b.crumb @route="authenticated.admin.tas.approval-queue" @label="Applications" />
        <b.crumb
          @route="authenticated.admin.tuition-assistance.program-instances.show"
          @label={{@model.programName}}
        />
      </TioPageBreadcrumbs>
      <div class="mt-2">
        <TioAppearance
          @apiKey={{ENV.apiKey}}
          @apiHost={{ENV.apiHost}}
          @token={{this.session.data.authenticated.access_token}}
          @type="tas_program_instance"
          {{! @glint-expect-error}}
          @documentId={{@model.id}}
          class="inline-block"
        />
      </div>
    </div>

    <HStack>
      <Section>
        <VStack @collapsed={{true}}>
          <TuitionAssistanceApplicationDetailsEmployeeInformation
            @instance={{@model}}
            @profileRoute="authenticated.admin.employees.show"
            @routeModel={{@model.employee.id}}
          />
          <TuitionAssistanceProgramDetailsProgramInfoCard
            @programInstance={{@model}}
            @showAttachments={{true}}
            class="w-full"
          />
          <TuitionAssistanceProgramDetailsApplicationInformation
            @application={{@model}}
            @isAdminView={{true}}
            @canRequestRepayment={{this.sessionContext.user.isAccountOwnerOrEmployeeAdmin}}
            @isReadonlyView={{false}}
            @requestingApp="employee"
            class="w-full"
          />
        </VStack>
      </Section>
      <Section>
        <VStack @collapsed={{true}}>
          <ApproverComments
            @commentable={{@model}}
            @commentableType="TasProgramInstance"
            @authoredByPerson={{this.sessionContext.user.person}}
            @commentTags={{array "approver"}}
          />
          <TuitionAssistanceApplicationDetailsApproverOverview
            @programTemplate={{@model.tasProgramTemplate}}
          />
        </VStack>
      </Section>
    </HStack>
    <Section>
      <:header>
        {{t "tuition_assistance.program_details.attachments"}}
      </:header>
      <:actions>
        <button type="button" {{on "click" (fn (mut this.uploadModalOpen) true)}}>
          <PhCloudArrowUp @size="28" class="mb-2 text-ocean-600" />
        </button>
      </:actions>
      <:body>
        {{#if this.allAttachments.length}}
          <AttachmentList
            @assets={{this.allAttachments}}
            @disabled={{false}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <p class="text-sm text-tio-gray-500">
            {{t "tuition_assistance.program_details.no_attachments"}}
          </p>
        {{/if}}
      </:body>
    </Section>
    <TuitionAssistanceApplicationDetailsApprovalQueueStatus
      @application={{@model}}
      @consumingApp="employee"
      @isReadonlyView={{false}}
    />
    <Section>
      <TuitionAssistanceApplicationDetailsProgramStatusApproval
        @instance={{@model}}
        @routePrefix="authenticated.admin"
      />
      <PaymentDetailsDrawer @programInstance={{@model}} />
    </Section>
    <TuitionAssistanceProgramDetailsAppDetailsAppHistory
      @application={{@model}}
      @viewType="admin"
    />

    <Modal @isOpen={{this.uploadModalOpen}} @onClose={{fn (mut this.uploadModalOpen) false}}>
      <:header>{{t "tuition_assistance.program_details.upload_attachments"}}</:header>
      <:body>
        <label for="classification-type" class="font-semibold text-sm text-tio-gray-700 block mb-1">
          {{t "tuition_assistance.program_details.courses.document_type"}}
        </label>
        <ReSelect
          data-test-classification-type
          @items={{classificationOptions}}
          @selected={{this.selectedClassification}}
          @onChange={{this.setUploadClassification}}
          @id="classification-type"
          @clearable={{false}}
        />
        <TuitionAssistanceFormsUploadAttachments
          @programTemplate={{@model.tasProgramTemplate}}
          @classification={{this.uploadClassificationField}}
          @attachable={{@model}}
          @attachableType="tasProgramInstance"
          @disabled={{false}}
          @hideAttachments={{true}}
        />
      </:body>
    </Modal>
  </template>
}
